import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { combineLatest, Observable } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { Component, inject, OnInit, Input } from '@angular/core';

import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';

import { ModalsService } from 'src/app/core/services/modals/modals.service';
import { ModalScrollBlockBase } from '@core/base/modalScrollBlock/modal-scroll-block.base';
import { MarketplaceService } from '@core/services/marketplace/marketplace.service';
import { TranslateService } from '@ngx-translate/core';
import { ComoService } from '@core/services/como/como.service';
import { AlertModalComponent } from '@shared/modals/alert-modal/alert-modal.component';
import { MarketplaceStoreRefiner } from '@common/refiners/marketplace.refiner';
import { ComoPluginType } from '@core/models/plugin.model';
import { AnalyticsService } from '@core/services/app/analytics.service';

@Component({
  selector: 'app-como-create-or-link-account-modal',
  templateUrl: './como-create-or-link-account-modal.component.html',
  standalone: false,
})
export class ComoCreateOrLinkAccountModalComponent extends ModalScrollBlockBase implements OnInit {
  static handle = 'como-create-or-link-account-modal';

  @Input() backgroundColor?: IContentBuilderFieldColor;

  private readonly modalSelector = inject(ModalStoreSelector);
  private readonly modalsService = inject(ModalsService);
  private readonly settingsSelector = inject(SettingsStoreSelector);
  private readonly marketplaceService = inject(MarketplaceService);
  private readonly translateService = inject(TranslateService);
  private readonly comoService = inject(ComoService);
  private readonly marketplaceStoreRefiner = inject(MarketplaceStoreRefiner);
  private readonly analyticsService = inject(AnalyticsService);

  readonly isDesktop = this.settingsSelector.selectDeviceIsDesktopScreenSignal;

  readonly index$: Observable<number> = this.modalSelector
    .selectModal(ComoCreateOrLinkAccountModalComponent.handle)
    .pipe(map(el => (el?.index ? el.index : 4)));

  step = 'attach-with-email';
  comoPlugin: ComoPluginType | undefined;
  phoneOrEmail = '';

  ngOnInit(): void {
    this.analyticsService.trackLoyaltyModalDisplay('como', 'create_or_link_account');
    this.comoPlugin = this.marketplaceService.getPluginByType('como');
    if (!this.comoPlugin || this.comoPlugin.plugin_enabled !== true) {
      this.modalsService.open(AlertModalComponent.handle);
      this.modalsService.setData(AlertModalComponent.handle, {
        message: this.translateService.instant('como.loyalty-plugin-not-configured'),
      });
      return;
    }

    // Only close when we have definitive values: do not close on undefined (loading state)
    combineLatest([
      this.marketplaceStoreRefiner.selectIsComoPluginEnabled,
      this.comoService.isUserComoAccountEnabledOnMarketplaceLevel$(),
    ])
      .pipe(
        take(1),
        tap(([isMarketplaceComoEnabled, isUserComoEnabled]) => {
          const shouldClose = isMarketplaceComoEnabled === false || isUserComoEnabled === true;
          if (shouldClose) {
            this.modalsService.close(ComoCreateOrLinkAccountModalComponent.handle);
          }
        }),
      )
      .subscribe();
  }

  goToStep(step: string) {
    this.analyticsService.trackLoyaltyStepChange('como', this.step, step);
    if (step === 'confirmation') {
      // reload page to reload user and blocks displayed
      document.location.reload();
      this.close();
    }

    this.step = step;
  }

  setPhoneOrEmail(phoneOrEmail: string) {
    this.phoneOrEmail = phoneOrEmail;
  }

  close(): void {
    this.analyticsService.trackLoyaltyButtonClick('como', 'close_modal');
    this.modalsService.close(ComoCreateOrLinkAccountModalComponent.handle);
  }
}
